/**
 * How many team members — managers and agents with their own sign-in — a
 * client may have (``app.modules.team``). 0 means the client has no team at
 * all: no Team tab, no assignment, and every member's session ends at once.
 *
 * The client manages its members itself; Connect only sets the seats. Lowering
 * them below the members already active is refused by the server with how many
 * there are — which to disable is the client's decision.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, Button, Card, CardBody, CardHeader, Input, QueryPending, api, toast, useCan } from '@connect/web-shared';

interface Seats {
  seats: number;
  seats_used: number;
}

export function TeamSeatsCard({ clientPublicId }: { clientPublicId: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('crm.portal_access.edit');
  const endpoint = `/crm/clients/${clientPublicId}/team`;
  const key = ['clients', clientPublicId, 'team-seats'];
  const seats = useQuery({ queryKey: key, queryFn: () => api.get<Seats>(endpoint) });
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (seats.data) setDraft(String(seats.data.seats));
  }, [seats.data]);

  const save = useMutation({
    mutationFn: () => api.put<Seats>(endpoint, { seats: Number(draft) }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success(data.seats ? `${data.seats} team seats` : 'Team switched off');
    },
    onError: (error) =>
      toast.error('Seats not changed', error instanceof ApiError ? error.message : undefined),
  });

  if (seats.data === undefined) return <QueryPending query={seats} />;
  const data = seats.data;
  if (!data) return null;
  const valid = /^\d{1,3}$/.test(draft) && Number(draft) <= 500;

  return (
    <Card>
      <CardHeader
        title="Team seats"
        description={
          data.seats
            ? `${data.seats_used} of ${data.seats} seats in use. The client adds its managers and agents under Team in its app.`
            : 'No team. Give seats and the client can add managers and agents who sign in with their own email or mobile.'
        }
      />
      {canEdit ? (
        <CardBody className="flex items-end gap-2">
          <Input
            label="Seats"
            inputMode="numeric"
            value={draft}
            onChange={(event) => setDraft(event.target.value.replace(/\D/g, ''))}
            hint="0 switches the team off and signs every member out."
            containerClassName="w-40"
          />
          <Button
            size="sm"
            loading={save.isPending}
            disabled={!valid || Number(draft) === data.seats}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </CardBody>
      ) : null}
    </Card>
  );
}
